import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Button, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, } from '@airia/ui';
import { ChevronDown } from 'lucide-react';
import { useAddinChatActions } from '../AddinChatActionsContext';
import { MessageCheckboxesSchema, parseDropdownOptions } from './schemas';
/**
 * Renders `<airiaMessageCheckboxes>` as a dropdown of toggleable options.
 * On Send, the selected `message` fields are joined by newline and submitted
 * as a single user message.
 */
export function MessageCheckboxes(props) {
    const { children, artifactContent, buttonText, sendButtonText, isLoading } = MessageCheckboxesSchema.parse(props);
    const actions = useAddinChatActions();
    const optionsSource = artifactContent || children;
    const parsedOptions = useMemo(() => parseDropdownOptions(optionsSource), [optionsSource]);
    const [items, setItems] = useState(() => parsedOptions.map((o) => ({ ...o, selected: false })));
    // Resync items if the parsed options change mid-stream. Preserve existing
    // selection where indices overlap to avoid silent flicker when the agent
    // appends new options at the end.
    useEffect(() => {
        setItems((prev) => {
            const same = prev.length === parsedOptions.length &&
                prev.every((p, i) => p.label === parsedOptions[i].label && p.message === parsedOptions[i].message);
            if (same)
                return prev;
            return parsedOptions.map((o, i) => ({
                ...o,
                selected: prev[i]?.selected ?? false,
            }));
        });
    }, [parsedOptions]);
    if (items.length === 0)
        return null;
    const disabled = !actions || actions.isExecuting || isLoading;
    const selectedMessages = items.filter((item) => item.selected).map((item) => item.message);
    const sendLabel = sendButtonText.length > 0 ? sendButtonText : 'Send';
    const triggerLabel = buttonText.length > 0 ? buttonText : 'Choose options';
    const handleSend = () => {
        if (!actions || selectedMessages.length === 0)
            return;
        const combined = selectedMessages.join('\n');
        void actions.submitMessage(combined);
        setItems((prev) => prev.map((item) => ({ ...item, selected: false })));
    };
    return (_jsxs(DropdownMenu, { modal: false, children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsxs(Button, { size: "sm", variant: "outline", disabled: disabled, children: [triggerLabel, _jsx(ChevronDown, { className: "ms-1 h-4 w-4", "aria-hidden": "true" })] }) }), _jsxs(DropdownMenuContent, { align: "start", children: [items.map((item, index) => (_jsx(DropdownMenuCheckboxItem, { checked: item.selected, onCheckedChange: (checked) => {
                            setItems((prev) => prev.map((p, i) => i === index ? { ...p, selected: checked === true } : p));
                        }, onSelect: (event) => event.preventDefault(), children: item.label }, index))), _jsx(DropdownMenuSeparator, {}), _jsx(DropdownMenuItem, { disabled: selectedMessages.length === 0, onSelect: handleSend, children: sendLabel })] })] }));
}
